import { GOOGLE_OAUTH_CLIENT_ID } from '../config.js';

/**
 * Thin wrapper over Google Identity Services (GIS) for the OAuth token model.
 *
 * Why GIS and not gapi.auth2? gapi.auth2 was deprecated by Google in 2022.
 * GIS is the supported successor — small SDK (~25kB), modern callback API,
 * tokens delivered straight to the JS callback.
 *
 * Token lifecycle:
 *   - Tokens last 1 hour. We cache {token, expiry, email} in localStorage and
 *     reuse it across page loads while it's still valid.
 *   - Why cache at all? GIS's requestAccessToken() ALWAYS opens a popup
 *     window — with prompt:'' and prior consent it auto-closes in ~300ms,
 *     but that flash on every refresh is jarring. With the cache, Google is
 *     only contacted when the token is actually stale (≤ once an hour).
 *   - Risk trade-off: the token is scoped to drive.appdata only and dies
 *     within the hour; caching it is the standard GIS-without-backend move.
 *   - On expiry / 401 from Drive, refreshToken() drops the cache and
 *     re-requests silently.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SCOPE = 'https://www.googleapis.com/auth/drive.appdata openid email';
const CONSENT_FLAG = 'shell.googleSync.consented';
const TOKEN_CACHE_KEY = 'shell.googleSync.tokenCache';
/** Treat tokens as expired this long before Google does — covers clock skew
 *  and a sync pass that starts just before the real expiry. */
const EXPIRY_MARGIN_MS = 5 * 60_000;

interface TokenClient {
  requestAccessToken(opts?: { prompt?: '' | 'consent' | 'select_account' }): void;
  callback: (resp: { access_token?: string; expires_in?: number; error?: string; error_description?: string }) => void;
}

interface TokenCache {
  token: string;
  /** ms since epoch when Google expires the token. */
  expiresAtMs: number;
  email: string | null;
}

function readTokenCache(): TokenCache | null {
  try {
    const raw = localStorage.getItem(TOKEN_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw) as TokenCache;
    if (!cache.token || typeof cache.expiresAtMs !== 'number') return null;
    if (Date.now() >= cache.expiresAtMs - EXPIRY_MARGIN_MS) return null;
    return cache;
  } catch {
    return null;
  }
}

function writeTokenCache(token: string, expiresInSec: number | undefined, email: string | null): void {
  const expiresAtMs = Date.now() + (expiresInSec ?? 3600) * 1000;
  try {
    localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({ token, expiresAtMs, email } satisfies TokenCache));
  } catch {
    // Storage full / blocked — worst case we fall back to per-load refresh.
  }
}

function clearTokenCache(): void {
  localStorage.removeItem(TOKEN_CACHE_KEY);
}

interface GoogleNamespace {
  accounts: {
    oauth2: {
      initTokenClient(opts: {
        client_id: string;
        scope: string;
        callback: (resp: { access_token?: string; error?: string }) => void;
      }): TokenClient;
      revoke(token: string, done?: () => void): void;
    };
  };
}

let gisPromise: Promise<GoogleNamespace> | null = null;
let tokenClient: TokenClient | null = null;
let accessToken: string | null = null;
let accountEmail: string | null = null;

function loadGis(): Promise<GoogleNamespace> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).google));
      existing.addEventListener('error', () => reject(new Error('Failed to load GIS')));
      if ((window as any).google?.accounts?.oauth2) resolve((window as any).google);
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve((window as any).google);
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisPromise;
}

function ensureClientId(): string {
  if (!GOOGLE_OAUTH_CLIENT_ID) {
    throw new Error('Google OAuth client ID not configured. Set VITE_GOOGLE_CLIENT_ID.');
  }
  return GOOGLE_OAUTH_CLIENT_ID;
}

async function ensureTokenClient(): Promise<TokenClient> {
  if (tokenClient) return tokenClient;
  const google = await loadGis();
  tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: ensureClientId(),
    scope: SCOPE,
    callback: () => {/* replaced per-call below */},
  });
  return tokenClient;
}

/**
 * Pop a Google sign-in flow. Returns true on success.
 * Sets a localStorage consent flag for future silent resumes.
 */
export async function signIn(): Promise<boolean> {
  const client = await ensureTokenClient();
  return new Promise<boolean>((resolve) => {
    client.callback = async (resp) => {
      if (resp.error || !resp.access_token) {
        console.warn('[googleAuth] sign-in failed', resp.error);
        resolve(false);
        return;
      }
      accessToken = resp.access_token;
      localStorage.setItem(CONSENT_FLAG, '1');
      try { await fetchUserinfo(); } catch { /* email is optional */ }
      writeTokenCache(resp.access_token, resp.expires_in, accountEmail);
      resolve(true);
    };
    client.requestAccessToken({ prompt: 'consent' });
  });
}

/**
 * Try to re-acquire a token silently. Only works if the user previously
 * consented for this origin. Returns true on success.
 *
 * Served from the localStorage cache while the token is still valid —
 * requestAccessToken() always flashes a popup window (even with prompt:''),
 * so we only call it when the cached token is missing or stale.
 */
export async function trySilentResume(): Promise<boolean> {
  if (!hasPriorConsent()) return false;

  const cached = readTokenCache();
  if (cached) {
    accessToken = cached.token;
    accountEmail = cached.email;
    return true;
  }

  const client = await ensureTokenClient();
  return new Promise<boolean>((resolve) => {
    client.callback = async (resp) => {
      if (resp.error || !resp.access_token) {
        // Silent failure (e.g. user revoked) — clear the flag so we don't
        // keep trying forever.
        if (resp.error === 'access_denied' || resp.error === 'consent_required') {
          localStorage.removeItem(CONSENT_FLAG);
        }
        resolve(false);
        return;
      }
      accessToken = resp.access_token;
      try { await fetchUserinfo(); } catch { /* email is optional */ }
      writeTokenCache(resp.access_token, resp.expires_in, accountEmail);
      resolve(true);
    };
    // Empty prompt = silent if possible, no UI if no prior grant.
    client.requestAccessToken({ prompt: '' });
  });
}

export async function signOut(): Promise<void> {
  const token = accessToken;
  accessToken = null;
  accountEmail = null;
  localStorage.removeItem(CONSENT_FLAG);
  clearTokenCache();
  if (token) {
    try {
      const google = await loadGis();
      await new Promise<void>((resolve) => google.accounts.oauth2.revoke(token, () => resolve()));
    } catch (err) {
      console.warn('[googleAuth] revoke failed', err);
    }
  }
}

export function getAccessToken(): string | null { return accessToken; }
export function getAccountEmail(): string | null { return accountEmail; }
export function hasPriorConsent(): boolean { return localStorage.getItem(CONSENT_FLAG) === '1'; }

// ─── Module-facing OAuth (backs Shell.googleApi on web) ──────────────────────

const SCOPE_TOKEN_PREFIX = 'shell.google.scopeToken.';

/**
 * Gets an access token for a Google scope (Calendar, Tasks, Sheets,
 * Gmail, …) — called internally by the web googleApi bridge.
 *
 * Per-scope-set tokens are cached in localStorage like the Drive token, so
 * repeat calls within the hour are popup-free. First-time scopes trigger
 * Google's consent popup (requires a reasonably fresh user gesture —
 * browsers may block the popup otherwise).
 */
export async function getAccessTokenForScopes(scopes: string): Promise<{ ok: boolean; token?: string; error?: string }> {
  const wanted = scopes.trim();
  if (!wanted) return { ok: false, error: 'No scopes requested' };

  const cacheKey = SCOPE_TOKEN_PREFIX + wanted;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const c = JSON.parse(raw) as { token?: string; expiresAtMs?: number };
      if (c.token && typeof c.expiresAtMs === 'number' && Date.now() < c.expiresAtMs - EXPIRY_MARGIN_MS) {
        return { ok: true, token: c.token };
      }
    }
  } catch { /* treat as cache miss */ }

  let google: GoogleNamespace;
  try {
    ensureClientId();
    google = await loadGis();
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
  // A dedicated client per scope set — GIS binds scopes at init time.
  const client = google.accounts.oauth2.initTokenClient({
    client_id: ensureClientId(),
    scope: wanted,
    callback: () => {/* replaced per-call below */},
  });
  const request = (prompt: '' | 'consent'): Promise<{ token?: string; expiresIn?: number; error?: string }> =>
    new Promise((resolve) => {
      client.callback = (resp) => resolve({
        token: resp.access_token,
        expiresIn: resp.expires_in,
        error: resp.error,
      });
      client.requestAccessToken({ prompt });
    });

  // Silent first (no consent UI if previously granted), then interactive.
  let resp = await request('');
  if (!resp.token) resp = await request('consent');
  if (!resp.token) return { ok: false, error: resp.error || 'Google authorization was cancelled' };

  try {
    localStorage.setItem(cacheKey, JSON.stringify({
      token: resp.token,
      expiresAtMs: Date.now() + (resp.expiresIn ?? 3600) * 1000,
    }));
  } catch { /* cache write is best-effort */ }
  return { ok: true, token: resp.token };
}

// ─── Shared-folder scope (F2 multi-user data sharing) ───────────────────────
// Shared module data lives in a visible Drive folder; joining one someone
// else shared requires the full Drive scope (drive.file can't reach files
// this app didn't create/open). The scope is granted incrementally — an
// explicit user action in Settings — and its token is cached per-scope by
// the getAccessTokenForScopes machinery above. While the OAuth consent
// screen is in Testing mode this works for registered test users.

const SHARED_SCOPE = 'https://www.googleapis.com/auth/drive';
const SHARED_CONSENT_FLAG = 'shell.googleSync.sharedConsented';

export function hasSharedConsent(): boolean {
  return localStorage.getItem(SHARED_CONSENT_FLAG) === '1';
}

/** Interactive grant for the shared-folder scope (call from a user gesture). */
export async function requestSharedScope(): Promise<boolean> {
  const res = await getAccessTokenForScopes(SHARED_SCOPE);
  if (res.ok) localStorage.setItem(SHARED_CONSENT_FLAG, '1');
  return res.ok;
}

/**
 * Token for shared-folder I/O, silent-only — safe to call from a background
 * sync (no popup risk). Returns null when the scope was never granted or the
 * silent refresh failed; callers should then skip the shared pass.
 */
export async function getSharedToken(): Promise<string | null> {
  if (!hasSharedConsent()) return null;

  // Fresh cached token from the per-scope cache?
  try {
    const raw = localStorage.getItem(SCOPE_TOKEN_PREFIX + SHARED_SCOPE);
    if (raw) {
      const c = JSON.parse(raw) as { token?: string; expiresAtMs?: number };
      if (c.token && typeof c.expiresAtMs === 'number' && Date.now() < c.expiresAtMs - EXPIRY_MARGIN_MS) {
        return c.token;
      }
    }
  } catch { /* cache miss */ }

  let google: GoogleNamespace;
  try {
    ensureClientId();
    google = await loadGis();
  } catch {
    return null;
  }
  const client = google.accounts.oauth2.initTokenClient({
    client_id: ensureClientId(),
    scope: SHARED_SCOPE,
    callback: () => {/* replaced below */},
  });
  return new Promise((resolve) => {
    client.callback = (resp: { access_token?: string; expires_in?: number; error?: string }) => {
      if (!resp.access_token) { resolve(null); return; }
      try {
        localStorage.setItem(SCOPE_TOKEN_PREFIX + SHARED_SCOPE, JSON.stringify({
          token: resp.access_token,
          expiresAtMs: Date.now() + (resp.expires_in ?? 3600) * 1000,
        }));
      } catch { /* best-effort */ }
      resolve(resp.access_token);
    };
    client.requestAccessToken({ prompt: '' });
  });
}

/** Drops the shared-scope token cache after a 401 so the next call re-requests. */
export function invalidateSharedToken(): void {
  localStorage.removeItem(SCOPE_TOKEN_PREFIX + SHARED_SCOPE);
}

/**
 * Called after a 401 from Drive — drop the cached token and request a fresh
 * one silently. Returns true if we got one.
 */
export async function refreshToken(): Promise<boolean> {
  accessToken = null;
  clearTokenCache(); // force a real re-request — the cached token just 401'd
  return trySilentResume();
}

async function fetchUserinfo(): Promise<void> {
  if (!accessToken) return;
  const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) return;
  const json = await res.json() as { email?: string };
  if (json.email) accountEmail = json.email;
}
